import React from 'react';
import styled from '@emotion/styled';
import { useModal } from 'context/ModalContext';
import { useContent } from 'context/ContentContext';

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SummaryHeader = styled.div`
  padding: 1.6rem 2rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.offWhiteDark};
`;

const SummaryTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;
  letter-spacing: -0.02em;
`;

const SummaryContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.6rem 2rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.offWhiteDark};
    border-radius: 3px;
  }
`;

const ProductInfo = styled.div`
  padding-bottom: 1.2rem;
  margin-bottom: 1.2rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.offWhiteDark};
`;

const BaseItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.6rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const ModifiersList = styled.div`
  margin-bottom: 1.6rem;
`;

const ModifierItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 0;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.black};
  opacity: 0.7;
`;

const ModifierName = styled.span`
  flex: 1;
  padding-right: 1rem;
`;

const ModifierPrice = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.success};
`;

const SummaryFooter = styled.div`
  padding: 1.6rem 2rem;
  border-top: 2px solid ${({ theme }) => theme.colors.offWhiteDark};
  background: ${({ theme }) => theme.colors.offWhite};
`;

const QuantityRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.black};
  opacity: 0.6;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
  padding-top: 0.8rem;
  border-top: 1px solid ${({ theme }) => theme.colors.tanLight};
`;

const formatPrice = (price) => {
  if (typeof price === 'string') return price;
  return `$${(price / 100).toFixed(2)}`;
};

export const ModalSummary = () => {
  const { item, selectedModifiers, currentQuantity, calculateTotalPrice } =
    useModal();
  const { messages } = useContent();

  if (!item) return null;

  const calculateSubtotal = () => {
    let subtotal = item.price || 0;

    Object.entries(selectedModifiers).forEach(([groupId, selection]) => {
      const group = item.modifierGroups?.find((g) => g.id === groupId);
      if (group) {
        const selections = Array.isArray(selection) ? selection : [selection];
        selections.forEach((modId) => {
          const modifier = group.modifiers.find((m) => m.id === modId);
          if (modifier?.price) {
            subtotal += modifier.price;
          }
        });
      }
    });

    return subtotal;
  };

  const modifierItems = Object.entries(selectedModifiers).flatMap(
    ([groupId, selection]) => {
      const group = item.modifierGroups?.find((g) => g.id === groupId);
      if (!group) return [];

      const selections = Array.isArray(selection) ? selection : [selection];
      return selections
        .map((modId) => {
          const modifier = group.modifiers.find((m) => m.id === modId);
          if (!modifier) return null;
          return {
            name: modifier.name,
            price: modifier.price,
            key: `${groupId}-${modId}`,
          };
        })
        .filter(Boolean);
    },
  );

  const subtotal = calculateSubtotal();
  const total = calculateTotalPrice();

  return (
    <SummaryContainer>
      <SummaryHeader>
        <SummaryTitle>
          {messages?.modal?.orderSummary || 'Order Summary'}
        </SummaryTitle>
      </SummaryHeader>

      <SummaryContent>
        <ProductInfo>
          <BaseItem>
            <span>{item.name}</span>
            <span>{formatPrice(item.price || 0)}</span>
          </BaseItem>
        </ProductInfo>

        {modifierItems.length > 0 && (
          <ModifiersList>
            {modifierItems.map((modifier) => (
              <ModifierItem key={modifier.key}>
                <ModifierName>{modifier.name}</ModifierName>
                {modifier.price > 0 && (
                  <ModifierPrice>+{formatPrice(modifier.price)}</ModifierPrice>
                )}
              </ModifierItem>
            ))}
          </ModifiersList>
        )}
      </SummaryContent>

      <SummaryFooter>
        <QuantityRow>
          <span>{messages?.modal?.subtotal || 'Subtotal'}</span>
          <span>{formatPrice(subtotal)}</span>
        </QuantityRow>
        <QuantityRow>
          <span>{messages?.modal?.quantity || 'Quantity'}</span>
          <span>×{currentQuantity}</span>
        </QuantityRow>
        <TotalRow>
          <span>{messages?.modal?.total || 'Total'}</span>
          <span>{formatPrice(total)}</span>
        </TotalRow>
      </SummaryFooter>
    </SummaryContainer>
  );
};

export default ModalSummary;
